import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import { GIButton } from 'app-components';
import Dropdown from '../../../components/common/form/Dropdown';
import { DateRangePickerDropdown } from './common/components/date-range-picker-dropdown/DateRangePickerDropdown';
import { SpannedGridItem, StyledButtonContainer, StyledGrid, StyledGridItem } from './AccountActivity.styled';
import LogCallModal from './LogCallModal';
import useDisclosure from '../../../hooks/useDisclosure';
import { exportHistory, setFiltersData } from '../../../actions/account-activity/accountActivityActionCreators';
import { ConditionalRendering } from '../../../components/common/conditional-rendering';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import {
    IAccountActivityEvent,
    IAccountActivityFilterData,
    IAccountActivityInitialData,
    IOption,
} from './common/types';

interface IAccountActivityFiltersProps {
    categoryOptions: IOption[];
    eventTypeOptions: IOption[];
    refetchData: () => void;
    applyFilters: (data: IAccountActivityEvent[], filters?: IAccountActivityFilterData) => any;
}

interface IFilterDropdownProps {
    name: string;
    labelText: string;
    options: any[];
    value: string;
    handleChange: (value: string) => void;
}

const OPTIONS_ALL = { value: 'ALL', text: 'All' };

export const FilterDropdown: React.FC<IFilterDropdownProps> = ({ name, labelText, options, value, handleChange }) => {
    return (
        <Dropdown
            label={{
                labelText,
                labelClasses: ['p-0'],
                wrapperClasses: ['d-flex', 'flex-column', 'align-items-sm-start', 'text-left'],
            }}
            inputData={{
                name,
                inputClasses: ['w-100'],
                options,
                type: 'dropdown',
                value,
                isInputRequired: false,
            }}
            actions={{
                handleFieldChange: e => handleChange(e.target.value),
                handleFieldBlur: () => {},
            }}
        />
    );
};

const AccountActivityFilters: React.FC<IAccountActivityFiltersProps> = ({
    categoryOptions,
    eventTypeOptions,
    refetchData,
    applyFilters,
}) => {
    const {
        filters,
        activityData,
        initialData: filtersData,
    }: {
        filters: any;
        activityData: any;
        initialData: IAccountActivityInitialData;
    } = useAppSelector(({ accountActivity }) => accountActivity);
    const dispatch = useAppDispatch();
    const [createdByRoleOptions, setCreatedByRoleOptions] = useState([OPTIONS_ALL]);
    const { isOpen, onOpen, onClose } = useDisclosure(false);
    const { id: encHouseholdId } = useParams();

    const onLogCallModalClose = (isRefetchEvents: boolean) => {
        onClose();
        isRefetchEvents && refetchData();
    };

    const {
        memberHistoryPermissionMap: { memberAbtLogcall },
    } = filtersData;

    useEffect(() => {
        const createdByRole =
            Object.entries(filtersData?.roleList).map(([key, value]) => ({ text: `${value}`, value: value })) || [];
        setCreatedByRoleOptions([OPTIONS_ALL, ...createdByRole]);
    }, [filtersData]);

    const onExportHistory = () => {
        dispatch(exportHistory({ encHouseholdId }));
    };

    const updateFilter = (filter: any) => {
        const updatedFilers = { ...filters, ...filter };
        dispatch(setFiltersData({ filter, currentFilteredData: applyFilters(activityData, updatedFilers) }));
    };

    return (
        <>
            <div data-testid="activity-filters-container">
                <StyledGrid columns="3">
                    <StyledGridItem data-testid="category-filter">
                        <FilterDropdown
                            name="category"
                            labelText="member.account.activity.filter.label.category"
                            options={categoryOptions}
                            value={filters.category}
                            handleChange={newCategory => {
                                if (newCategory !== filters.category) {
                                    const updatedFilter = { category: newCategory, eventType: filters.eventType };
                                    updatedFilter.eventType = OPTIONS_ALL.value;
                                    updateFilter(updatedFilter);
                                }
                            }}
                        />
                    </StyledGridItem>
                    <StyledGridItem data-testid="event-type-filter">
                        <FilterDropdown
                            name="eventType"
                            labelText="member.account.activity.filter.label.eventType"
                            options={eventTypeOptions}
                            value={filters.eventType}
                            handleChange={newEventType => {
                                if (newEventType !== filters.eventType) {
                                    updateFilter({ eventType: newEventType });
                                }
                            }}
                        />
                    </StyledGridItem>
                    <StyledGridItem data-testid="created-by-role-filter">
                        <FilterDropdown
                            name="createdByRole"
                            labelText="member.account.activity.filter.label.createdByRole"
                            options={createdByRoleOptions}
                            value={filters.createdByRole}
                            handleChange={newCreatedByRole => {
                                if (newCreatedByRole !== filters.createdByRole) {
                                    updateFilter({ createdByRole: newCreatedByRole });
                                }
                            }}
                        />
                    </StyledGridItem>
                </StyledGrid>
                <StyledGrid columns="3">
                    <StyledGridItem data-testid="date-range-filter">
                        <DateRangePickerDropdown
                            onDropdownChange={val => {
                                if (val !== filters.dateRange) {
                                    updateFilter({ dateRange: val });
                                }
                            }}
                        />
                    </StyledGridItem>
                    <SpannedGridItem>
                        <StyledButtonContainer>
                            <GIButton
                                id="export_cap_history_btn"
                                labelId="member.account.activity.filter.button.export.cap.history"
                                variant="secondary"
                                onClickHandler={onExportHistory}
                            />
                            <ConditionalRendering isTrue={memberAbtLogcall}>
                                <GIButton
                                    id="log_a_call_btn"
                                    labelId="member.account.activity.filter.button.log.a.call"
                                    variant="secondary"
                                    onClickHandler={onOpen}
                                />
                            </ConditionalRendering>
                        </StyledButtonContainer>
                    </SpannedGridItem>
                </StyledGrid>
            </div>
            <LogCallModal
                isOpen={isOpen}
                onClose={onLogCallModalClose}
                consumerId={filtersData.householdId}
            />
        </>
    );
};

export default AccountActivityFilters;
